// src/features/projects/weekly/WeeklyOverview.jsx
//
// Projects > Updates. Single-page intake/readout: week picker + project rail
// beside an ordered detail pane (no inner tabs, no meeting mode).
//
// Live weeks show the active filtered hub population (projectIds prop).
// Frozen weeks render the ENTIRE archived snapshot regardless of filters -
// archive membership is never reconstructed from today's IDs.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { T } from "../../../app/tokens.js";
import { getWeeklyWeeks, getWeeklyOverview, getWeeklyRail } from "../../../data/projectWeeklyClient.js";
import { clearWeeklyReads, refreshWeeklyReads } from "../../../services/projectWeeklyReads.js";
import {
  SORT_MODE, UPDATES_SORT_OPTIONS, orderProjects, mergeStoredOrder, loadOrderPrefs, saveOrderPrefs,
  currentMondayUtc, weekLabel, weekMode, isWritableWeek,
} from "./weeklyModel.js";
import { weeklyParticipation } from "../../../lib/weeklyParticipation.js";
import { assignedTrackCompleteness } from "../../../lib/weeklyTrackCompleteness.js";
import { WeeklyProjectCard, canContribute } from "./WeeklyProjectCard.jsx";
import { PortfolioWeeklyOverview } from "./PortfolioWeeklyOverview.jsx";
import { ProjectRecordDrawer } from "../ProjectRecordDrawer.jsx";
import { WeeklyHealthDot } from "./WeeklyHealthDot.jsx";
import { btn, btnPrimary, muted, withDisabled } from "./weeklyUi.js";
import { proceedConfirmedNavigation } from "../recordNavigationGuard.js";
import { useReaderSelectionScroll } from "./useReaderSelectionScroll.js";
import { PrevNextPager } from "../../../components/PrevNextPager.jsx";
import { SortDropdown } from "../../../components/SortDropdown.jsx";
import { useUpdatesCustomOrder } from "./useUpdatesCustomOrder.js";

const SHADOW = T.listCardShadow;
// Selection sentinel for the pinned portfolio Overview; never a project ID,
// never in sortable order prefs, never counted as a project.
export const OVERVIEW = "overview";
const WEEKLY_CSS = `
[data-testid="projects-weekly-overview"] button:focus-visible, [data-testid="projects-weekly-overview"] select:focus-visible, [data-testid="projects-weekly-overview"] textarea:focus-visible, [data-testid="projects-weekly-overview"] input:focus-visible, .wk-av:focus-visible { outline: 2px solid ${T.blue}; outline-offset: 2px; }
.wk-work { display: grid; grid-template-columns: 300px minmax(0, 1fr); gap: 0; align-items: start; border: 1px solid ${T.border}; border-radius: 14px; overflow: clip; background: var(--v2-bg, ${T.lightGray}); }
.wk-rail { background: ${T.card}; border-right: 1px solid ${T.border}; position: sticky; top: var(--updates-sticky-top, 56px); max-height: calc(var(--wk-viewport-h, 100dvh) - var(--updates-sticky-top, 56px)); overflow: auto; }
.wk-prow { width: 100%; display: flex; align-items: center; gap: 11px; padding: 11px 16px; border: 0; border-bottom: 1px solid ${T.lightGray}; background: transparent; text-align: left; cursor: pointer; color: ${T.dark}; }
.wk-prow:hover { background: ${T.lightGray}; }
.wk-prow[aria-current="true"] { background: ${T.blueSoft}; }
.wk-li { position: relative; }
.wk-prow.wk-ov { background: ${T.projectsReferenceBg}; color: ${T.projectsHubNavy}; border-bottom-color: ${T.projectsReferenceBorder}; box-shadow: inset 3px 0 0 ${T.projectsReferenceGold}; }
.wk-prow.wk-ov:hover, .wk-prow.wk-ov[aria-current="true"] { background: ${T.projectsReferenceHover}; box-shadow: inset 4px 0 0 ${T.projectsReferenceGold}; }
.wk-prow.wk-ov:focus-visible { outline: 2px solid ${T.projectsReferenceText} !important; outline-offset: -4px !important; }
.wk-reference-icon { display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0; width: 22px; height: 22px; border-radius: 6px; background: ${T.card}; border: 1px solid ${T.projectsReferenceBorder}; color: ${T.projectsReferenceGold}; }
.wk-record { border: 0; background: transparent; color: ${T.blue}; padding: 0; display: inline-flex; align-items: center; gap: 5px; font-size: ${T.fsXs}px; font-weight: ${T.fwSemibold}; cursor: pointer; white-space: nowrap; flex-shrink: 0; }
.wk-record:hover { text-decoration: underline; }
.wk-headbottom { display: flex; align-items: flex-end; justify-content: space-between; gap: 12px; margin-top: 12px; }
.wk-headbottom > :first-child { min-width: 0; }
.wk-headbottom .wk-record { margin-left: auto; }
[data-testid="weekly-rail-overview-item"]::before { display: none; }
.wk-prow.wk-ov:disabled { opacity: .85; cursor: default; }
.wk-li[data-selected="true"]::before { content: ""; position: absolute; left: 0; top: 0; bottom: 0; width: 4px; background: ${T.blue}; pointer-events: none; z-index: 1; }
.wk-dhead { background: ${T.card}; padding: 10px 20px; border-bottom: 1px solid ${T.border}; border-left: 1px solid ${T.border}; position: sticky; top: var(--updates-sticky-top, 56px); z-index: 3; }
.wk-dhead { height: 82px; box-sizing: border-box; overflow: hidden; }
.wk-project-head { height: auto; min-height: 82px; overflow: visible; }
.wk-headline { height: 30px; display: flex; align-items: center; gap: 12px; min-width: 0; flex-wrap: nowrap; }
.wk-headcontrols { display: flex; align-items: center; gap: 8px; flex-shrink: 0; white-space: nowrap; margin-left: auto; }
.wk-headcontrols > * { flex-shrink: 0; }
.wk-headmeta { min-height: 25px; margin-top: 5px; flex-wrap: wrap; }
.wk-headmeta > * { min-width: 0; overflow-wrap: anywhere; }
.wk-headmeta > :first-child { flex-shrink: 0; }
.wk-grip { border: 0; background: transparent; color: ${T.light}; opacity: .28; cursor: grab; padding: 0 8px; display: inline-flex; align-items: center; border-bottom: 1px solid ${T.lightGray}; }
.wk-li:hover .wk-grip, .wk-li:focus-within .wk-grip { color: ${T.mid}; opacity: .6; }
.wk-grip[aria-pressed="true"] { color: ${T.blue}; }
li[data-drop="true"] { box-shadow: inset 0 2px 0 ${T.blue}; }
li[data-dragging="true"] { opacity: .45; }
.wk-body { padding: 16px 20px 48px; }
.wk-reader { container: updates-reader / inline-size; min-width: 0; }
.wk-layout { display: grid; grid-template-columns: minmax(0, 1fr); gap: 20px; align-items: start; }
.wk-imgcol { position: static; max-height: none; overflow: visible; overscroll-behavior: contain; min-width: 0; }
/* The app navigation and project rail consume viewport width too. Only reserve
   an image column when the actual reader can leave a comfortable main record. */
@container updates-reader (min-width: 820px) {
  .wk-layout { grid-template-columns: minmax(0, 1fr) 300px; }
  .wk-imgcol { position: sticky; top: calc(var(--updates-sticky-top, 56px) + var(--wk-head-h, 0px) + 16px); max-height: calc(var(--wk-viewport-h, 100dvh) - var(--updates-sticky-top, 56px) - var(--wk-head-h, 0px) - 32px); overflow: auto; }
}
.wk-ecard { background: ${T.card}; border: 1px solid ${T.border}; border-radius: 14px; box-shadow: ${SHADOW}; margin-bottom: 16px; overflow: hidden; }
.wk-ecard-hd { display: flex; align-items: center; justify-content: space-between; gap: 10px; padding: 12px 17px; border-bottom: 1px solid ${T.lightGray}; }
.wk-hero { border: 1px solid; border-radius: 14px; box-shadow: ${SHADOW}; margin-bottom: 16px; }
/* Keep controls in the tab order; keyboard focus reveals the containing card.
   Editors and feedback must never depend on hover, including after a failure. */
[data-testid="projects-weekly-overview"] :is(.wk-actions, .wk-action) { opacity: 0; pointer-events: none; transition: opacity .12s; }
[data-testid="projects-weekly-overview"] :is(.wk-ecard, .wk-hero):is(:hover, :focus-within, :has(input:not([type="file"]), textarea, select, [contenteditable="true"], [role="alert"], [role="status"])) :is(.wk-actions, .wk-action) { opacity: 1; pointer-events: auto; }
@media (hover: none), (any-pointer: coarse) {
  [data-testid="projects-weekly-overview"] :is(.wk-actions, .wk-action) { opacity: 1; pointer-events: auto; }
}
.wk-cols2 { display: grid; grid-template-columns: 1fr 1fr; gap: 22px; }
.wk-fin { display: grid; grid-template-columns: repeat(auto-fit, minmax(min(100%, 110px), 1fr)); gap: 12px; }
.wk-trackrow { display: flex; align-items: center; gap: 13px; }
.wk-av { position: relative; display: inline-flex; cursor: default; border-radius: 50%; }
.wk-avtip { position: absolute; bottom: 125%; left: 50%; transform: translateX(-50%); background: ${T.dark}; color: ${T.white}; font-size: 11px; font-weight: 600; padding: 5px 9px; border-radius: 7px; white-space: nowrap; opacity: 0; pointer-events: none; transition: opacity .14s; z-index: 10; }
.wk-av .wk-avtip { opacity: 1; }
@media (max-width: 860px) {
  .wk-work { grid-template-columns: minmax(0, 1fr); }
  .wk-dhead { position: static; }
  .wk-project-head .wk-headline { height: auto; min-height: 30px; flex-wrap: wrap; gap: 6px 12px; }
  .wk-project-head .wk-headcontrols { flex-wrap: wrap; white-space: normal; }
  .wk-headbottom { flex-wrap: wrap; }
  .wk-project-head .wk-headmeta { height: auto; min-height: 25px; flex-wrap: wrap; white-space: normal; }
  .wk-body { --wk-overall-top: var(--updates-sticky-top, 0px); }
  .wk-rail { position: static; max-height: 320px; border-right: 0; border-bottom: 1px solid ${T.border}; }
  .wk-cols2, .wk-fin { grid-template-columns: minmax(0, 1fr); }
  .wk-trackrow { flex-wrap: wrap; }
  .wk-dhead, .wk-body { padding-left: 14px; padding-right: 14px; }
}
/* Reserve the panel width only when controls wrap on small screens. Keeping
   the trigger at its right edge preserves the shared right-aligned anchor. */
@media (max-width: 600px) {
  [data-testid="weekly-sort"] { min-width: 240px; display: flex; justify-content: flex-end; }
}
`;

function Arrow({ dir }) {
  const d = dir === "left" ? "M7.5 3L4.5 6l3 3" : "M4.5 3l3 3-3 3";
  return <svg aria-hidden="true" width={12} height={12} viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" style={{ display: "block" }}><path d={d} /></svg>;
}
function Grip() {
  return <svg aria-hidden="true" width={10} height={14} viewBox="0 0 10 14" fill="currentColor" style={{ display: "block" }}>{[2, 7, 12].map((y) => <g key={y}><circle cx="2.5" cy={y} r="1.3" /><circle cx="7.5" cy={y} r="1.3" /></g>)}</svg>;
}
const navBtn = { ...btn, width: 28, height: 28, padding: 0, flexShrink: 0, display: "inline-flex", alignItems: "center", justifyContent: "center", color: T.mid };

function WeekBadge({ mode, overview, weekInfo }) {
  const map = {
    historical: [overview?.protectedWeek ? "Protected week - read-only" : "Past week - archive pending - read-only", T.mid, T.lightGray],
    legacy: ["Frozen before Weekly Review · content unavailable", T.mid, T.lightGray],
  };
  const m = map[mode];
  if (!m || !overview) return null;
  return <span data-testid="weekly-week-state" style={{ fontSize: T.fsXxs, fontWeight: T.fwSemibold, padding: "2px 8px", borderRadius: 6, color: m[1], background: m[2], whiteSpace: "nowrap" }}>{m[0]}</span>;
}

function Skeleton() {
  return (
    <div data-testid="weekly-loading" aria-busy="true" className="wk-work" style={{ minHeight: 360 }}>
      <div style={{ padding: 14, display: "flex", flexDirection: "column", gap: 10, background: T.card }}>
        {[0, 1, 2, 3, 4].map((j) => <div key={j} style={{ height: 34, borderRadius: 8, background: T.lightGray }} />)}
      </div>
      <div style={{ padding: 20, display: "flex", flexDirection: "column", gap: 14 }}>
        {[90, 140, 160].map((h, j) => <div key={j} style={{ height: h, borderRadius: 14, background: T.card, opacity: 0.7 }} />)}
      </div>
    </div>
  );
}

function RailRow({ project, on, onSelect, busy, reorder, index, count }) {
  const part = assignedTrackCompleteness(project);
  const [drop, setDrop] = useState(false);
  const canDrag = Boolean(reorder) && !busy;
  const onKey = (e) => {
    if (!reorder || busy) return;
    const to = e.key === "ArrowUp" ? index - 1 : e.key === "ArrowDown" ? index + 1 : e.key === "Home" ? 0 : e.key === "End" ? count - 1 : null;
    if (to === null) return;
    e.preventDefault();
    if (to >= 0 && to < count && to !== index) reorder.moveTo(project.id, to, { focus: true });
  };
  return (
    <li data-testid={`weekly-rail-item-${project.id}`} className="wk-li" data-selected={on ? "true" : undefined} style={{ display: "flex", alignItems: "stretch" }}
      data-drop={drop ? "true" : undefined} data-dragging={reorder?.dragId === project.id ? "true" : undefined}
      onDragOver={reorder ? (e) => { if (reorder.dragId && reorder.dragId !== project.id) { e.preventDefault(); setDrop(true); } } : undefined}
      onDragLeave={reorder ? () => setDrop(false) : undefined}
      onDrop={reorder ? (e) => { e.preventDefault(); setDrop(false); const id = reorder.dragId; reorder.setDragId(null); if (id && id !== project.id) reorder.moveTo(id, index); } : undefined}>
      {reorder && (
        <button type="button" className="wk-grip" data-record-nav-ignore ref={(el) => reorder.registerGrip(project.id, el)} data-testid={`weekly-grip-${project.id}`} disabled={busy}
          draggable={canDrag}
          onDragStart={canDrag ? (e) => { e.dataTransfer.effectAllowed = "move"; try { e.dataTransfer.setData("text/plain", project.id); } catch { /* jsdom */ } reorder.setDragId(project.id); } : undefined}
          onDragEnd={() => { reorder.setDragId(null); setDrop(false); }}
          aria-label={`Reorder ${project.name}, position ${index + 1} of ${count}. Use arrow keys to move.`} title="Drag to reorder, or focus and use arrow keys" onKeyDown={onKey}
          style={{ background: on ? T.blueSoft : "transparent" }}><Grip /></button>
      )}
      <button type="button" className="wk-prow" data-testid={`weekly-rail-${project.id}`} aria-current={on ? "true" : undefined} disabled={busy} onClick={() => onSelect(project.id)}>
        {project.signalsPending ? <span aria-label="Health loading" style={{ width: 9, height: 9, background: T.lightGray, borderRadius: "50%" }} /> : <WeeklyHealthDot project={project} surface="rail" />}
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: "block", fontSize: T.fsSm, fontWeight: T.fwSemibold, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{project.name}</span>
          <span style={{ display: "block", fontSize: T.fsXxs, color: T.mid, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{project.phaseLabel || "Phase not set"}</span>
        </span>
        <span style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 3 }}>
          <span data-testid={`weekly-rail-count-${project.id}`} title={project.signalsPending ? "Assigned track participation loading" : part.available ? `${part.inCount} of ${part.total} assigned tracks reported or quiet this week` : "Assigned track participation not captured"} style={{ fontSize: T.fsXxs, fontWeight: T.fwBold, color: T.light, whiteSpace: "nowrap" }}>
            {project.signalsPending ? <span aria-label="Participation loading">…</span> : part.available ? <><b style={{ color: part.inCount ? T.blue : T.light }}>{part.inCount}</b>/{part.total} in</> : "n/a"}
          </span>
        </span>
      </button>
    </li>
  );
}

export function WeeklyOverview({ projectIds, populationReady = true, authIdentity = "unbound", connectionsByProjectId, onOpenMyUpdate, refreshToken = 0, initialWeekDate, initialProjectId, onWeekChange, selectedWeekDate, onUrlStateChange, canWrite = true, onMetricsChange, onToolbarChange, onAcknowledgedSave, currentRole = "viewer", onProjectUpdated, onArchived }) {
  const [weeksState, setWeeksState] = useState({ loading: true, error: null, weeks: [], userKey: null, currentWeek: null });
  const [localWeek, setLocalWeek] = useState(initialWeekDate || null);
  const followsCurrentWeek = useRef(!initialWeekDate);
  const [rail, setRail] = useState(null);
  const urlControlled = typeof onUrlStateChange === "function";
  const railData = rail?.identity === authIdentity ? rail.data : null;
  const discoveredCurrent = [railData?.currentWeek, weeksState.identity === authIdentity ? weeksState.currentWeek : null].filter(Boolean).sort().at(-1);
  const selectedWeek = urlControlled ? selectedWeekDate || discoveredCurrent : localWeek || discoveredCurrent;
  useEffect(() => { if (selectedWeek) onWeekChange?.(selectedWeek); }, [selectedWeek, onWeekChange]);
  const selectedWeekRef = useRef(selectedWeek);
  selectedWeekRef.current = selectedWeek;
  const [ov, setOv] = useState({ loading: false, error: null, data: null });
  // When URL-controlled, the URL's weeklyProject is the only selection
  // authority (back/forward, including its absence). Local state is used only
  // in uncontrolled hosts.
  const [localSelectedId, setSelectedId] = useState(initialProjectId || null);
  const selectedId = urlControlled ? (initialProjectId || null) : localSelectedId;
  const overviewRef = useRef(null);
  const holdSelectionScroll = useReaderSelectionScroll(overviewRef, selectedId, selectedWeek);
  // The shell scrollport excludes its fixed title/navigation. Sizing rails to
  // 100dvh would hide their last rows below that scrollport's bottom edge.
  useEffect(() => {
    const element = overviewRef.current;
    const scrollport = element?.closest(".v2-content");
    const measure = () => element?.style.setProperty("--wk-viewport-h", `${scrollport?.clientHeight || window.innerHeight}px`);
    measure();
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
    if (scrollport) observer?.observe(scrollport);
    window.addEventListener("resize", measure);
    return () => { observer?.disconnect(); window.removeEventListener("resize", measure); };
  }, []);
  useEffect(() => {
    if (initialProjectId) { if (!urlControlled) setSelectedId(initialProjectId); overviewRef.current?.focus({ preventScroll: true }); }
  }, [initialProjectId, urlControlled]);
  const [sortChoice, setSortChoice] = useState(null);
  const [sortOpen, setSortOpen] = useState(false);
  const sortRef = useRef(null);
  // This ref follows the actual control, including when React portals it out
  // of the reader. Checking overviewRef would dismiss clicks inside the menu.
  useEffect(() => {
    if (!sortOpen) return;
    const outside = (event) => {
      if (!sortRef.current?.contains(event.target)) setSortOpen(false);
    };
    const escape = (event) => {
      if (event.key !== "Escape" || event.defaultPrevented) return;
      setSortOpen(false);
      sortRef.current?.querySelector("[data-dropdown-trigger]")?.focus();
    };
    document.addEventListener("mousedown", outside);
    document.addEventListener("focusin", outside);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("mousedown", outside);
      document.removeEventListener("focusin", outside);
      document.removeEventListener("keydown", escape);
    };
  }, [sortOpen]);
  const [mutationBusy, setMutationBusy] = useState(false);
  const mutationRef = useRef(false);
  const reqRef = useRef(0);
  const railReq = useRef(0);
  const weeksReq = useRef(0);
  const requestedWeek = urlControlled ? selectedWeekDate : initialWeekDate || localWeek;
  const loadRail = useCallback(async (wk) => {
    const id = ++railReq.current;
    try {
      const data = await getWeeklyRail(wk || null);
      if (id === railReq.current) setRail({ identity: authIdentity, data });
      return data;
    } catch {
      // The full overview remains the authoritative error/retry boundary.
      return null;
    }
  }, [authIdentity]);
  useEffect(() => {
    const counter = railReq;
    void loadRail(requestedWeek);
    return () => { counter.current++; };
  }, [requestedWeek, loadRail]);

  const loadWeeks = useCallback(async () => {
    const request = ++weeksReq.current;
    const identity = authIdentity;
    try {
      const res = await getWeeklyWeeks();
      if (request !== weeksReq.current) return;
      const currentWeek = res?.currentWeek || currentMondayUtc();
      setWeeksState({ identity, loading: false, error: null, weeks: Array.isArray(res?.weeks) ? res.weeks : [], userKey: res?.userKey ?? null, currentWeek });
      setLocalWeek((w) => followsCurrentWeek.current ? currentWeek : w || currentWeek);
    } catch (err) {
      if (request !== weeksReq.current) return;
      setWeeksState((s) => ({ ...s, loading: false, error: err?.message || "Could not load weeks." }));
    }
  }, [authIdentity]);
  useEffect(() => {
    const counter = weeksReq;
    loadWeeks();
    return () => { counter.current++; };
  }, [loadWeeks]);

  const loadOverview = useCallback(async (wk, { quiet = false } = {}) => {
    if (!wk) return;
    const id = ++reqRef.current;
    setOv((s) => ({ identity: authIdentity, loading: !quiet, error: null, data: quiet && s.identity === authIdentity ? s.data : null }));
    try {
      // Lightweight membership takes the week lock first; never queue the rail
      // behind our own heavy read. Discovery is independent of both.
      await loadRail(wk);
      if (id !== reqRef.current) return;
      const data = await getWeeklyOverview(wk);
      if (id === reqRef.current) setOv({ identity: authIdentity, loading: false, error: null, data });
    } catch (err) {
      if (id === reqRef.current) setOv((s) => ({ identity: authIdentity, loading: false, error: err?.message || "Could not load Updates.", data: quiet ? s.data : null }));
    }
  }, [authIdentity, loadRail]);
  useEffect(() => {
    const counter = reqRef;
    loadOverview(selectedWeek);
    return () => { counter.current++; };
  }, [selectedWeek, loadOverview]);
  useEffect(() => {
    const refresh = () => {
      if (mutationRef.current) return;
      refreshWeeklyReads();
      void loadWeeks();
      void loadRail(selectedWeekRef.current);
      if (selectedWeekRef.current) void loadOverview(selectedWeekRef.current, { quiet: true });
    };
    const timer = window.setInterval(refresh, 60_000);
    const untilRollover = Date.parse(`${discoveredCurrent}T00:00:00Z`) + 7 * 86400000 - Date.now();
    const rollover = untilRollover > 0 && untilRollover <= 7 * 86400000
      ? window.setTimeout(refresh, untilRollover + 20) : null;
    window.addEventListener("focus", refresh);
    return () => { window.clearInterval(timer); window.clearTimeout(rollover); window.removeEventListener("focus", refresh); };
  }, [loadWeeks, loadOverview, loadRail, discoveredCurrent]);

  const lastRefresh = useRef(refreshToken);
  useEffect(() => {
    if (lastRefresh.current === refreshToken) return;
    lastRefresh.current = refreshToken;
    const cw = ov.data?.currentWeek || weeksState.currentWeek;
    if (selectedWeek && selectedWeek === cw && !ov.data?.frozen) {
      clearWeeklyReads();
      loadOverview(selectedWeek, { quiet: true });
    }
  }, [refreshToken]); // eslint-disable-line react-hooks/exhaustive-deps

  const userKey = (ov.identity === authIdentity ? ov.data?.userKey : null) ?? railData?.userKey
    ?? (weeksState.identity === authIdentity ? weeksState.userKey : null);
  const prefScope = JSON.stringify([authIdentity, userKey]);
  const localPrefs = useMemo(() => loadOrderPrefs(userKey), [userKey, authIdentity]); // eslint-disable-line react-hooks/exhaustive-deps
  const customOrder = useUpdatesCustomOrder(userKey, authIdentity);
  const prefs = useMemo(() => ({
    mode: sortChoice?.scope === prefScope ? sortChoice.mode : localPrefs.mode,
    ids: customOrder.ids,
  }), [sortChoice, prefScope, localPrefs.mode, customOrder.ids]);

  const currentWeek = [ov.identity === authIdentity ? ov.data?.currentWeek : null, discoveredCurrent].filter(Boolean).sort().at(-1) || currentMondayUtc();
  const detailData = ov.identity === authIdentity && ov.data?.weekDate === selectedWeek ? ov.data : null;
  const data = detailData || (railData?.weekDate === selectedWeek ? railData : null);
  const mode = weekMode(data, currentWeek);
  const writable = Boolean(detailData) && isWritableWeek(detailData, currentWeek);
  const frozen = Boolean(data?.frozen);

  const idsKey = Array.isArray(projectIds) ? projectIds.join(",") : null;
  const population = useMemo(() => {
    const all = Array.isArray(data?.projects) ? data.projects : [];
    if (!populationReady && !frozen) return [];
    if (frozen || idsKey === null) return all;
    const allow = new Set(idsKey ? idsKey.split(",") : []);
    return all.filter((p) => allow.has(p.id));
  }, [data, frozen, idsKey, populationReady]);

  const ordered = useMemo(() => orderProjects(population, prefs.mode, prefs.ids), [population, prefs]);
  const overviewOn = selectedId === OVERVIEW;
  const fullCollection = useMemo(() => orderProjects(Array.isArray(data?.projects) ? data.projects : [], prefs.mode, prefs.ids), [data, prefs]);
  // A project opened from the Overview rollup stays selected even when the
  // rail filter hides it; otherwise fall back to the first visible row.
  // An explicitly requested project that is not in this week's data is shown
  // as unavailable - never silently replaced by the first row or Overview.
  const requested = overviewOn ? null : ordered.find((p) => p.id === selectedId) || (mode !== "legacy" && fullCollection.find((p) => p.id === selectedId)) || null;
  const requestedMissing = Boolean(selectedId) && !overviewOn && !requested;
  const selected = overviewOn ? null : requested || (selectedId ? null : ordered[0] || null);
  const selIndex = selected ? ordered.indexOf(selected) : -1;

  const weekOptions = useMemo(() => {
    const list = [...weeksState.weeks];
    for (const w of [currentWeek, selectedWeek]) if (w && !list.some((x) => x.weekDate === w)) list.push({ weekDate: w, frozen: false, frozenAt: null });
    return list.sort((a, b) => b.weekDate.localeCompare(a.weekDate));
  }, [weeksState.weeks, currentWeek, selectedWeek]);
  const weekIdx = weekOptions.findIndex((w) => w.weekDate === selectedWeek);
  const weekInfo = weekOptions[weekIdx];

  // Unsaved record drafts block project/week switches until confirmed.
  const recordGuard = useRef(null);
  const drawerGuard = useRef(null);
  const isDirty = () => Boolean(recordGuard.current?.() || drawerGuard.current?.()?.dirty);
  const [pendingNav, setPendingNav] = useState(null);
  const selectionNow = useRef(null);
  selectionNow.current = { week: selectedWeek, ids: fullCollection.map(project => project.id), rail: ordered.map(project => project.id) };
  const guarded = (go) => { if (mutationRef.current) return; if (isDirty()) setPendingNav(previous => previous || go); else go(); };
  const selectProject = (id) => {
    if (mutationRef.current) return;
    if (overviewOn ? id === OVERVIEW : id === selected?.id) return;
    const fromRail = selectionNow.current.rail.includes(id);
    guarded(() => {
      const now = selectionNow.current;
      if (mutationRef.current || now.week !== selectedWeek ||
          (id !== OVERVIEW && !(fromRail ? now.rail : now.ids).includes(id))) return;
      holdSelectionScroll(); if (urlControlled) onUrlStateChange({ weeklyProject: id }); else setSelectedId(id);
    });
  };
  // Shared record drawer: snapshot of the project + week context at open.
  const [drawer, setDrawer] = useState(null);
  const drawerKey = `${selectedWeek}|${selected?.id || ""}`;
  const switchDrawerProject = (id) => {
    const target = ordered.find(project => project.id === id);
    if (!target || !drawer || mutationRef.current || recordGuard.current?.()) return;
    holdSelectionScroll();
    setDrawer({ key: `${selectedWeek}|${id}`, projectId: id, name: target.name,
      part: weeklyParticipation(target), frozen, readOnly: frozen || !(writable && canWrite) });
    if (urlControlled) onUrlStateChange({ weeklyProject: id }); else setSelectedId(id);
  };
  useEffect(() => {
    // Week/project changed underneath (e.g. back/forward): close a clean
    // drawer; a dirty one stays on its own project so nothing is discarded.
    setDrawer((d) => (d && d.key !== drawerKey && !drawerGuard.current?.()?.dirty ? null : d));
  }, [drawerKey]);
  useEffect(() => {
    const onBefore = (e) => { if (isDirty()) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", onBefore);
    return () => window.removeEventListener("beforeunload", onBefore);
  }, []);
  const changeWeek = (w) => {
    if (!w || w === selectedWeek || mutationRef.current) return;
    guarded(() => { if (urlControlled) onUrlStateChange({ week: w }); else { followsCurrentWeek.current = false; setLocalWeek(w); } });
  };
  const setSortMode = (m) => {
    setSortOpen(false);
    setSortChoice({ scope: prefScope, mode: m });
    saveOrderPrefs(userKey, { mode: m });
  };
  // Report the actual rail population (including frozen membership), not the
  // parent's current inventory or selected-week KPI population.
  const toolbarCallback = useRef(onToolbarChange);
  toolbarCallback.current = onToolbarChange;
  const sortResetRef = useRef(null);
  sortResetRef.current = () => setSortMode(SORT_MODE.AZ);
  const resetToolbarSort = useCallback(() => sortResetRef.current?.(), []);
  const toolbarSortActive = prefs.mode !== SORT_MODE.AZ;
  const collectionTotal = Array.isArray(data?.projects) ? data.projects.length : 0;
  useEffect(() => {
    toolbarCallback.current?.({
      count: data && (populationReady || frozen) ? population.length : null, total: data ? collectionTotal : null,
      sortActive: toolbarSortActive, resetSort: resetToolbarSort,
    });
  }, [population.length, collectionTotal, toolbarSortActive, resetToolbarSort, data, populationReady, frozen]);

  // Custom-mode reorder (drag or keyboard on the grip). Writes through the
  // existing user-scoped order prefs; hidden IDs keep their stored slots.
  const [dragId, setDragId] = useState(null);
  const grips = useRef(new Map());
  const pendingFocus = useRef(null);
  const moveTo = (id, toIndex, { focus = false } = {}) => {
    const before = ordered.map((p) => p.id);
    const visible = before.filter((x) => x !== id);
    if (visible.length === ordered.length) return;
    visible.splice(Math.max(0, Math.min(toIndex, visible.length)), 0, id);
    if (visible.every((x, i) => x === before[i])) return;
    const fullIds = orderProjects(data?.projects || [], SORT_MODE.CUSTOM, prefs.ids).map((p) => p.id);
    const nextIds = mergeStoredOrder(fullIds, visible);
    if (focus) pendingFocus.current = id;
    customOrder.reorder(nextIds);
  };
  useEffect(() => {
    if (!pendingFocus.current) return;
    grips.current.get(pendingFocus.current)?.focus();
    pendingFocus.current = null;
  });
  const reorder = prefs.mode === SORT_MODE.CUSTOM ? {
    moveTo, dragId, setDragId,
    registerGrip: (id, el) => { if (el) grips.current.set(id, el); else grips.current.delete(id); },
  } : null;

  // Parent metrics: full unfiltered selected-week overview (never the rail
  // population). Callbacks held in refs so parent re-renders cannot loop.
  const metricsCb = useRef(onMetricsChange);
  metricsCb.current = onMetricsChange;
  const ackCb = useRef(onAcknowledgedSave);
  ackCb.current = onAcknowledgedSave;
  const projectCb = useRef(onProjectUpdated);
  projectCb.current = onProjectUpdated;
  const retryRef = useRef(null);
  retryRef.current = () => {
    if (weeksState.error) { setWeeksState((st) => ({ ...st, loading: true, error: null })); void loadWeeks(); }
    clearWeeklyReads();
    void loadRail(selectedWeekRef.current);
    if (selectedWeekRef.current) void loadOverview(selectedWeekRef.current, { quiet: Boolean(ov.data) });
  };
  const stableRetry = useCallback(() => retryRef.current?.(), []);
  const metricsData = detailData;
  useEffect(() => {
    const error = ov.error || (!metricsData ? weeksState.error : null) || null;
    metricsCb.current?.({ loading: !error && Boolean(ov.loading || !metricsData), error, data: metricsData, retry: stableRetry });
  }, [ov.loading, ov.error, weeksState.error, metricsData, stableRetry]);

  const onMutate = useCallback(async (fn) => {
    if (mutationRef.current) throw new Error("A change is still saving. Please wait before trying again.");
    mutationRef.current = true;
    setMutationBusy(true);
    try {
      await fn();
      clearWeeklyReads();
      if (selectedWeekRef.current === selectedWeek) await loadOverview(selectedWeek, { quiet: true });
      try { ackCb.current?.(); } catch { /* parent refresh is best-effort */ }
    } catch (err) {
      try { ackCb.current?.(); } catch { /* partial saves may have landed */ }
      // Earlier steps of a multi-step save may have been acknowledged, so
      // always refresh projections; local drafts are not re-seeded by this.
      clearWeeklyReads();
      if (selectedWeekRef.current === selectedWeek) { try { await loadOverview(selectedWeek, { quiet: true }); } catch { /* keep original error */ } }
      throw err;
    } finally {
      mutationRef.current = false;
      setMutationBusy(false);
    }
  }, [loadOverview, selectedWeek]);

  const prevDisabled = mutationBusy || weekIdx < 0 || weekIdx >= weekOptions.length - 1;
  const nextDisabled = mutationBusy || weekIdx <= 0;
  const projectNav = selected && (
    <span data-testid="weekly-project-pager" style={{ display: "inline-flex", gap: 6, alignItems: "center", whiteSpace: "nowrap" }}>
      <span data-testid="weekly-project-position" style={{ ...muted, marginRight: 4 }}>{selIndex < 0 ? "Outside current filter" : `${selIndex + 1} of ${ordered.length}`}{!writable ? " · read-only" : ""}</span>
      <PrevNextPager ids={ordered.map(project => project.id)} currentId={selected.id} onNavigate={selectProject}
        compact showUnavailable busy={mutationBusy} disabled={Boolean(pendingNav)}
        keyboardActive={!drawer} previousLabel="Previous project" nextLabel="Next project"
        previousTestId="weekly-project-prev" nextTestId="weekly-project-next" />
    </span>
  );
  const sortControl = <div ref={sortRef} data-testid="weekly-sort">
    <SortDropdown options={UPDATES_SORT_OPTIONS} selectedKey={prefs.mode} defaultKey={SORT_MODE.AZ}
      isOpen={sortOpen} onToggle={() => setSortOpen((open) => !open)} onSelect={setSortMode} />
  </div>;
  const sortHost = typeof document !== "undefined" ? document.getElementById("updates-toolbar-sort") : null;

  return (
    <section ref={overviewRef} tabIndex={-1} data-testid="projects-weekly-overview" aria-label="Updates" style={{ marginTop: 4, outline: "none" }}>
      <style>{WEEKLY_CSS}</style>
      {sortHost ? createPortal(sortControl, sortHost) : sortControl}
      {customOrder.error && <div role="status" style={{ ...muted, marginBottom: 6 }}>
        {customOrder.error} <button type="button" style={btn} onClick={customOrder.retry}>Retry</button>
      </div>}
      {pendingNav && (
        <div role="alertdialog" aria-label="Unsaved project record changes" data-testid="weekly-record-unsaved" style={{ display: "flex", gap: 8, alignItems: "center", padding: "8px 14px", borderRadius: 8, background: T.utilAmber, fontSize: T.fsSm, marginBottom: 8, flexWrap: "wrap" }}>
          <span style={{ flex: 1, minWidth: 0 }}>This project has an open editor. Switching now discards its unsaved changes.</span>
          <button type="button" data-testid="weekly-record-keep" onClick={() => setPendingNav(null)} style={btn}>Keep editing</button>
          <button type="button" data-testid="weekly-record-discard" disabled={mutationBusy || Boolean(drawerGuard.current?.()?.busy)} onClick={() => {
            if (mutationRef.current || drawerGuard.current?.()?.busy) return;
            const go = pendingNav;
            setPendingNav(null); setDrawer(null);
            proceedConfirmedNavigation(go);
          }} style={btn}>Discard and switch</button>
        </div>
      )}
      {mutationBusy && <div role="status" style={{ ...muted, marginBottom: 6 }}>Saving. Please wait before switching week or project.</div>}
      {weeksState.error && !data && <ErrorBox message={weeksState.error} onRetry={() => { setWeeksState((s) => ({ ...s, loading: true, error: null })); loadWeeks(); }} />}
      {ov.error && <ErrorBox message={ov.error} onRetry={stableRetry} />}
      {data && mode === "historical" && !data.protectedWeek && (
        <p data-testid="weekly-historical-note" style={{ ...muted, fontSize: T.fsSm, margin: "0 0 10px" }}>
          This past week has no authoritative archive yet. Displayed data is read-only, not original historical evidence.
        </p>
      )}
      {!data && !ov.error && !weeksState.error && <Skeleton />}

      {data && (
        <div className="wk-work" data-testid="weekly-agenda">
          <nav aria-label="Projects this week" data-testid="weekly-rail" className="wk-rail">
            <div style={{ position: "sticky", top: 0, zIndex: 2, background: T.card, padding: "12px 14px", borderBottom: `1px solid ${T.lightGray}`, display: "flex", flexDirection: "column", gap: 8 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <button type="button" aria-label="Previous week (older)" title="Previous week (older)" data-testid="weekly-week-prev" disabled={prevDisabled} onClick={() => changeWeek(weekOptions[weekIdx + 1]?.weekDate)} style={withDisabled(navBtn, prevDisabled)}><Arrow dir="left" /></button>
                <span data-testid="weekly-week-label" data-week={selectedWeek} data-live={writable} aria-live="polite" aria-busy={ov.loading || mutationBusy} aria-label={`${weekLabel(selectedWeek)}${writable ? " — current live week" : frozen ? " — frozen read-only week" : " — read-only week"}`} style={{ flex: 1, minWidth: 0, display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "center", gap: "2px 5px", fontSize: T.fsXs, fontWeight: T.fwSemibold, color: T.dark }}>
                  <span>{weekLabel(selectedWeek)}</span>
                  {writable && <span data-testid="weekly-live-label" style={{ display: "inline-flex", alignItems: "center", gap: 3, fontSize: T.fsXxs, color: T.greenDeep, fontWeight: T.fwSemibold }}><span data-testid="weekly-live-dot" aria-hidden="true" style={{ width: 6, height: 6, borderRadius: "50%", background: T.greenDeep, flexShrink: 0 }} />LIVE</span>}
                </span>
                <button type="button" aria-label="Next week (newer)" title="Next week (newer)" data-testid="weekly-week-next" disabled={nextDisabled} onClick={() => changeWeek(weekOptions[weekIdx - 1]?.weekDate)} style={withDisabled(navBtn, nextDisabled)}><Arrow dir="right" /></button>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "space-between", flexWrap: "wrap" }}>
                <WeekBadge mode={mode} overview={data} weekInfo={weekInfo} />
              </div>
              {onOpenMyUpdate && canWrite && (
                <button type="button" data-testid="weekly-my-update" disabled={!writable} title={writable ? "Opens your current-week update; posting requires your assignments" : "Posting is available only in the current unfrozen week"} onClick={() => { if (writable && canWrite) onOpenMyUpdate(selected?.id || null); }} style={withDisabled({ ...btnPrimary, width: "100%", height: T.toolbarCtrlH, boxSizing: "border-box", padding: "0 10px", display: "flex", alignItems: "center", justifyContent: "center" }, !writable)}>Start my weekly update</button>
              )}
              {reorder && <div data-testid="weekly-custom-hint" style={{ ...muted, fontSize: T.fsXxs, color: T.light }}>Custom order · drag to arrange</div>}
            </div>
            <div className="wk-li" data-selected={overviewOn ? "true" : undefined} data-testid="weekly-rail-overview-item" style={{ borderBottom: `1px solid ${T.border}` }}>
              <button type="button" className="wk-prow wk-ov" data-testid="weekly-rail-overview" aria-current={overviewOn ? "true" : undefined} disabled={mutationBusy} onClick={() => selectProject(OVERVIEW)} style={{ paddingLeft: reorder ? 42 : 16 }}>
                <span className="wk-reference-icon" aria-hidden="true">◎</span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: "block", fontSize: T.fsSm, fontWeight: T.fwBold }}>Overview</span>
                  <span style={{ display: "block", fontSize: T.fsXxs, color: T.mid }}>Whole portfolio · {fullCollection.length} project{fullCollection.length === 1 ? "" : "s"}</span>
                </span>
              </button>
            </div>
            {mode !== "legacy" && ordered.length > 0 && (
              <ul data-testid="weekly-rail-list" aria-label={reorder ? "Projects, custom order - drag the grip or use arrow keys to reorder" : undefined} style={{ listStyle: "none", margin: 0, padding: 0 }}>
                {ordered.map((p, i) => <RailRow key={p.id} project={p} on={p.id === selected?.id} onSelect={selectProject} busy={mutationBusy} index={i} count={ordered.length} reorder={reorder} />)}
              </ul>
            )}
          </nav>

          <div data-testid="weekly-reader-content" style={{ minWidth: 0 }}>
            {!detailData || (!populationReady && !frozen) ? (
              <div data-testid="weekly-detail-loading" aria-busy="true" role="status" style={{ padding: 24 }}>
                {ov.error ? "Updates details are unavailable. Retry to load this week." : "Loading Updates…"}
                <div style={{ height: 180, marginTop: 16, background: T.card, borderRadius: 14 }} />
              </div>
            ) : overviewOn ? (
              <PortfolioWeeklyOverview key={selectedWeek} data={data} projects={mode === "legacy" ? [] : fullCollection} legacy={mode === "legacy"} weekDate={selectedWeek} writable={writable && canWrite} frozen={frozen} onMutate={onMutate} busy={mutationBusy} guardRef={recordGuard} onOpenProject={(id) => selectProject(id)} />
            ) : mode === "legacy" ? (
              <EmptyPane testId="weekly-legacy" title="Updates content was not captured for this archive" body="This week was frozen before Updates existed. The original WPR archive remains available in Reports; nothing here is rebuilt from today's data." />
            ) : requestedMissing ? (
              <EmptyPane testId="weekly-project-unavailable" title="This project is not in this week" body={frozen ? "The requested project was not captured in this week's archive. Pick another project or week." : "The requested project is not active in this week's Updates. Pick another project or week."} />
            ) : ordered.length === 0 && !selected ? (
              <EmptyPane testId="weekly-empty" title={frozen ? "This archive contains no projects" : "No active projects in view"} body={frozen ? "The snapshot for this week was frozen with an empty project list." : "Adjust the Projects filters or search above to bring projects into this week."} />
            ) : selected && (
              <WeeklyProjectCard key={`${selectedWeek}-${selected.id}`} project={selected} currentConnections={connectionsByProjectId?.get(selected.id)} writable={writable && canWrite} liveWeek={writable} frozen={frozen} weekDate={selectedWeek} onMutate={onMutate} nav={projectNav} userKey={userKey} currentRole={currentRole} guardRef={recordGuard} onOpenMyUpdate={writable && canWrite && canContribute(selected) ? onOpenMyUpdate : undefined} onOpenRecord={() => setDrawer({ key: drawerKey, projectId: selected.id, name: selected.name, part: weeklyParticipation(selected), frozen, readOnly: frozen || !(writable && canWrite) })} />
            )}
          </div>
        </div>
      )}
      <ProjectRecordDrawer open={Boolean(drawer)} projectId={drawer?.projectId} projectName={drawer?.name} origin="updates"
        participation={drawer?.part ? { status: "ok", part: drawer.part } : null}
        frozen={Boolean(drawer?.frozen)} readOnly={Boolean(drawer?.readOnly)} currentRole={currentRole} guardRef={drawerGuard}
        onClose={() => setDrawer(null)}
        onArchived={(id) => {
          setDrawer(null);
          onArchived?.(id);
          if (selectedWeekRef.current) void loadOverview(selectedWeekRef.current, { quiet: true });
        }}
        orderedProjectIds={ordered.map(project => project.id)}
        navigationDisabled={mutationBusy || Boolean(recordGuard.current?.())}
        onOpenProject={switchDrawerProject}
        onRecordSaved={(updated) => { if (selectedWeekRef.current) void loadOverview(selectedWeekRef.current, { quiet: true }); try { if (updated) projectCb.current?.(updated); ackCb.current?.(); } catch { /* best-effort */ } }} />
    </section>
  );
}

function EmptyPane({ testId, title, body }) {
  return (
    <div data-testid={testId} style={{ margin: 24, border: `1px dashed ${T.borderDark || T.border}`, borderRadius: 14, padding: "28px 24px", background: T.card }}>
      <div style={{ fontSize: T.fsBase, fontWeight: T.fwBold, color: T.dark, marginBottom: 4 }}>{title}</div>
      <p style={{ margin: 0, ...muted, fontSize: T.fsSm, lineHeight: 1.5 }}>{body}</p>
    </div>
  );
}

function ErrorBox({ message, onRetry }) {
  return (
    <div role="alert" data-testid="weekly-error" style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", borderRadius: 8, background: T.utilRed, border: `1px solid ${T.redRing}`, marginBottom: 10 }}>
      <span style={{ fontSize: T.fsSm, color: T.dark, flex: 1 }}>{message}</span>
      <button type="button" data-testid="weekly-retry" onClick={onRetry} style={btn}>Retry</button>
    </div>
  );
}
